// src/pages/lms/LMSRegister.jsx — Updated with document upload
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Building2, Search, CheckCircle, AlertCircle, Upload, X, FileText } from 'lucide-react';

export default function LMSRegister() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const [institutions,    setInstitutions]    = useState([]);
  const [filteredInsts,   setFilteredInsts]   = useState([]);
  const [instSearch,      setInstSearch]      = useState('');
  const [selectedInst,    setSelectedInst]    = useState(null);
  const [countries,       setCountries]       = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');

  const [contactName,  setContactName]  = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  // Document upload
  const [docFile,     setDocFile]     = useState(null);
  const [docUploading,setDocUploading]= useState(false);
  const [docUrl,      setDocUrl]      = useState('');
  const [docName,     setDocName]     = useState('');

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    supabase.from('institutions')
      .select('id,name,city,country,type,category,rank_in_country')
      .order('country').order('rank_in_country')
      .then(({ data }) => {
        if (data) {
          setInstitutions(data);
          setCountries([...new Set(data.map(i => i.country))].sort());
        }
      });
  }, []);

  useEffect(() => {
    let f = institutions;
    if (selectedCountry) f = f.filter(i => i.country === selectedCountry);
    if (instSearch.trim()) {
      const q = instSearch.toLowerCase();
      f = f.filter(i => i.name.toLowerCase().includes(q));
    }
    setFilteredInsts(f);
  }, [institutions, selectedCountry, instSearch]);

  // Upload verification document
  const handleDocUpload = async (file) => {
    if (!file) return;
    setDocUploading(true);
    setError('');
    try {
      const ext  = file.name.split('.').pop();
      const path = `registrations/${Date.now()}_${file.name}`;
      const { error: upErr } = await supabase.storage
        .from('lms-documents')
        .upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage
        .from('lms-documents')
        .getPublicUrl(path);
      setDocUrl(urlData?.publicUrl || '');
      setDocName(file.name);
      setDocFile(file);
    } catch (e) {
      setError('Document upload failed. Please try again.');
    } finally {
      setDocUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedInst)        { setError('Please select your institution'); return; }
    if (!contactName.trim())  { setError('Please enter contact name'); return; }
    if (!contactEmail.trim()) { setError('Please enter contact email'); return; }
    if (!docUrl)              { setError('Please upload your verification document'); return; }

    setLoading(true);
    setError('');
    try {
      // Check already registered
      const { data: existing } = await supabase
        .from('institution_accounts')
        .select('id,verification_status')
        .eq('institution_id', selectedInst.id)
        .maybeSingle();

      if (existing) {
        if (existing.verification_status === 'approved') {
          setError('This institution is already registered. Please login.');
        } else {
          setError('A registration request already exists. Please wait for approval.');
        }
        setLoading(false);
        return;
      }

      // Generate unique username
      const nameParts = selectedInst.name
        .toLowerCase().replace(/[^a-z0-9\s]/g, '').split(' ').filter(Boolean);
      const base = nameParts.slice(0, 3).join('_').slice(0, 20);
      let username = base;
      let suffix   = 1;
      while (true) {
        const { data: uc } = await supabase
          .from('institution_accounts')
          .select('id').eq('lms_username', username).maybeSingle();
        if (!uc) break;
        username = `${base}_${suffix++}`;
      }

      await supabase.from('institution_accounts').insert({
        institution_id:              selectedInst.id,
        lms_username:                username,
        assigned_username:           username,
        contact_name:                contactName.trim(),
        contact_email:               contactEmail.trim().toLowerCase(),
        contact_phone:               contactPhone.trim(),
        verification_document_url:   docUrl,
        verification_document_name:  docName,
        verification_status:         'pending',
        is_active:                   false,
        is_verified:                 false,
      });

      setSuccess(true);
      setStep(3);
    } catch (err) {
      setError(err.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  const s = {
    page:  { minHeight:'100vh', background:'linear-gradient(135deg,#0F172A,#1E293B)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 },
    card:  { background:'#fff', borderRadius:24, width:'100%', maxWidth:580, overflow:'hidden', boxShadow:'0 25px 60px rgba(0,0,0,0.3)' },
    head:  { background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', padding:'32px 32px 24px' },
    body:  { padding:32 },
    lbl:   { fontSize:13, fontWeight:600, color:'#374151', marginBottom:6, display:'block' },
    inp:   { width:'100%', padding:'11px 14px', borderRadius:10, border:'1.5px solid #E5E7EB', fontSize:14, color:'#111827', outline:'none', boxSizing:'border-box' },
    btn:   { width:'100%', padding:'13px 0', borderRadius:12, border:'none', background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:'#fff', fontSize:15, fontWeight:700, cursor:'pointer' },
  };

  const stepDot = (active, done) => ({
    width:32, height:32, borderRadius:'50%',
    background: done ? '#10B981' : active ? '#4F8EF7' : 'rgba(255,255,255,0.2)',
    color:'#fff', display:'flex', alignItems:'center',
    justifyContent:'center', fontSize:13, fontWeight:700,
  });

  return (
    <div style={s.page}>
      <div style={s.card}>
        {/* Header */}
        <div style={s.head}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:18 }}>
            <div style={{ width:44, height:44, borderRadius:12, background:'rgba(255,255,255,0.2)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Building2 size={22} color="#fff" />
            </div>
            <div>
              <div style={{ color:'rgba(255,255,255,0.7)', fontSize:12, fontWeight:600 }}>UNIVERSE LMS</div>
              <div style={{ color:'#fff', fontSize:20, fontWeight:800 }}>Institution Registration</div>
            </div>
          </div>
          {/* Steps */}
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {[{n:1,l:'Select'},{n:2,l:'Details'},{n:3,l:'Done'}].map((st, i) => (
              <div key={st.n} style={{ display:'flex', alignItems:'center', gap:8, flex: i<2 ? 1 : 'none' }}>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <div style={stepDot(step===st.n, step>st.n)}>{step>st.n?'✓':st.n}</div>
                  <span style={{ color: step>=st.n ? '#fff' : 'rgba(255,255,255,0.4)', fontSize:11, fontWeight:600 }}>{st.l}</span>
                </div>
                {i<2 && <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.3)' }} />}
              </div>
            ))}
          </div>
        </div>

        <div style={s.body}>
          {error && (
            <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'12px 14px', marginBottom:20, display:'flex', gap:8, alignItems:'flex-start' }}>
              <AlertCircle size={16} color="#EF4444" style={{ flexShrink:0, marginTop:1 }} />
              <span style={{ color:'#991B1B', fontSize:13 }}>{error}</span>
            </div>
          )}

          {/* STEP 1 */}
          {step === 1 && (
            <div>
              <h2 style={{ fontSize:18, fontWeight:800, color:'#111827', margin:'0 0 6px' }}>Select Your Institution</h2>
              <p style={{ color:'#6B7280', fontSize:13, margin:'0 0 20px' }}>Search from our global institution database</p>

              <label style={s.lbl}>Filter by Country</label>
              <select value={selectedCountry} onChange={e=>setSelectedCountry(e.target.value)}
                style={{ ...s.inp, marginBottom:14 }}>
                <option value="">All Countries</option>
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>

              <label style={s.lbl}>Search Institution</label>
              <div style={{ position:'relative', marginBottom:14 }}>
                <Search size={15} color="#9CA3AF" style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)' }} />
                <input value={instSearch} onChange={e=>setInstSearch(e.target.value)}
                  placeholder="Type institution name..."
                  style={{ ...s.inp, paddingLeft:36 }} />
              </div>

              <div style={{ border:'1.5px solid #E5E7EB', borderRadius:12, maxHeight:240, overflowY:'auto', marginBottom:20 }}>
                {filteredInsts.length === 0 ? (
                  <div style={{ padding:24, textAlign:'center', color:'#9CA3AF', fontSize:13 }}>No institutions found</div>
                ) : filteredInsts.slice(0,50).map((inst,i) => (
                  <div key={inst.id} onClick={() => setSelectedInst(inst)}
                    style={{ padding:'12px 16px', background: selectedInst?.id===inst.id ? '#EFF6FF' : '#fff', borderBottom: i<filteredInsts.length-1 ? '1px solid #F3F4F6' : 'none', cursor:'pointer', display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{ width:28, height:28, borderRadius:'50%', background: selectedInst?.id===inst.id ? '#4F8EF7' : '#E5E7EB', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color: selectedInst?.id===inst.id ? '#fff' : '#6B7280', flexShrink:0 }}>
                      {inst.rank_in_country || '#'}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:600, color: selectedInst?.id===inst.id ? '#1D4ED8' : '#111827' }}>{inst.name}</div>
                      <div style={{ fontSize:11, color:'#6B7280' }}>{inst.city} · {inst.country} · {inst.category}</div>
                    </div>
                    {selectedInst?.id===inst.id && <CheckCircle size={18} color="#4F8EF7" />}
                  </div>
                ))}
              </div>

              {selectedInst && (
                <div style={{ background:'#EFF6FF', border:'1.5px solid #BFDBFE', borderRadius:10, padding:'12px 14px', marginBottom:20 }}>
                  <div style={{ fontWeight:700, color:'#1D4ED8', fontSize:13 }}>✓ {selectedInst.name}</div>
                  <div style={{ color:'#3B82F6', fontSize:11, marginTop:2 }}>{selectedInst.city} · {selectedInst.country}</div>
                </div>
              )}

              <button onClick={() => { if (!selectedInst) { setError('Please select your institution'); return; } setError(''); setStep(2); }} style={s.btn}>
                Continue →
              </button>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div>
              <h2 style={{ fontSize:18, fontWeight:800, color:'#111827', margin:'0 0 6px' }}>Contact & Verification</h2>
              <p style={{ color:'#6B7280', fontSize:13, margin:'0 0 20px' }}>
                Registering: <strong>{selectedInst?.name}</strong>
              </p>

              <label style={s.lbl}>Contact Person Name *</label>
              <input value={contactName} onChange={e=>setContactName(e.target.value)}
                placeholder="e.g. Dr. Ahmed Ali (Registrar)" style={{ ...s.inp, marginBottom:14 }} />

              <label style={s.lbl}>Official Email Address *</label>
              <input type="email" value={contactEmail} onChange={e=>setContactEmail(e.target.value)}
                placeholder="e.g. registrar@university.edu.pk" style={{ ...s.inp, marginBottom:14 }} />

              <label style={s.lbl}>Phone Number</label>
              <input value={contactPhone} onChange={e=>setContactPhone(e.target.value)}
                placeholder="e.g. +92 300 1234567" style={{ ...s.inp, marginBottom:20 }} />

              {/* Document upload */}
              <label style={s.lbl}>Verification Document *</label>
              <div style={{ marginBottom:8, fontSize:12, color:'#6B7280', lineHeight:1.5 }}>
                Upload any official document proving your institution identity: official letterhead, registration certificate, accreditation document, or HEC recognition letter (PDF, DOC, JPG, PNG — max 10MB)
              </div>

              {!docFile ? (
                <label style={{ display:'block', cursor:'pointer' }}>
                  <input type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    style={{ display:'none' }}
                    onChange={e => e.target.files?.[0] && handleDocUpload(e.target.files[0])}
                  />
                  <div style={{ border:'2px dashed #BFDBFE', borderRadius:12, padding:'24px 20px', textAlign:'center', background:'#F0F9FF', cursor:'pointer' }}>
                    {docUploading ? (
                      <div style={{ color:'#3B82F6', fontSize:13, fontWeight:600 }}>Uploading...</div>
                    ) : (
                      <>
                        <Upload size={28} color="#3B82F6" style={{ margin:'0 auto 8px' }} />
                        <div style={{ color:'#1D4ED8', fontWeight:700, fontSize:13 }}>Click to upload document</div>
                        <div style={{ color:'#6B7280', fontSize:11, marginTop:4 }}>PDF, DOC, DOCX, JPG, PNG</div>
                      </>
                    )}
                  </div>
                </label>
              ) : (
                <div style={{ border:'1.5px solid #BBF7D0', borderRadius:10, padding:'12px 16px', background:'#F0FDF4', display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                  <FileText size={20} color="#10B981" />
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, color:'#065F46', fontSize:13 }}>Document uploaded ✓</div>
                    <div style={{ color:'#6B7280', fontSize:11 }}>{docName}</div>
                  </div>
                  <button onClick={() => { setDocFile(null); setDocUrl(''); setDocName(''); }}
                    style={{ background:'none', border:'none', cursor:'pointer', padding:4 }}>
                    <X size={16} color="#6B7280" />
                  </button>
                </div>
              )}

              <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:10, padding:'12px 14px', margin:'16px 0', fontSize:12, color:'#92400E', lineHeight:1.6 }}>
                ℹ️ After submission, admin reviews your document and assigns you a unique <strong>username and password</strong>. You will receive them at your email within 24–48 hours.
              </div>

              <div style={{ display:'flex', gap:10 }}>
                <button onClick={() => setStep(1)}
                  style={{ flex:1, padding:'13px 0', borderRadius:12, border:'1.5px solid #E5E7EB', background:'#fff', color:'#374151', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                  ← Back
                </button>
                <button onClick={handleSubmit} disabled={loading || docUploading}
                  style={{ ...s.btn, flex:2, opacity: loading||docUploading ? 0.7 : 1 }}>
                  {loading ? 'Submitting...' : 'Submit Registration'}
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div style={{ textAlign:'center', padding:'20px 0' }}>
              <div style={{ width:72, height:72, borderRadius:'50%', background:'linear-gradient(135deg,#10B981,#059669)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
                <CheckCircle size={36} color="#fff" />
              </div>
              <h2 style={{ fontSize:22, fontWeight:800, color:'#111827', margin:'0 0 8px' }}>Registration Submitted!</h2>
              <p style={{ color:'#6B7280', fontSize:14, lineHeight:1.6, margin:'0 0 24px' }}>
                Your request for <strong>{selectedInst?.name}</strong> has been submitted with your verification document.
                Admin will review and send login credentials to <strong>{contactEmail}</strong> within 24–48 hours.
              </p>
              <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:12, padding:16, marginBottom:24, textAlign:'left' }}>
                <div style={{ fontWeight:700, color:'#065F46', fontSize:13, marginBottom:8 }}>What happens next?</div>
                {[
                  'Admin reviews your institution details and document',
                  'Admin approves and assigns your unique username & password',
                  'You receive login credentials via email',
                  'Login at /lms/login to access your LMS dashboard',
                ].map((item,i) => (
                  <div key={i} style={{ display:'flex', gap:8, marginBottom:6, fontSize:12, color:'#065F46' }}>
                    <span style={{ fontWeight:700 }}>{i+1}.</span><span>{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/lms/login" style={{ display:'block', padding:'13px 0', borderRadius:12, background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:'#fff', fontSize:15, fontWeight:700, textDecoration:'none' }}>
                Go to LMS Login
              </Link>
            </div>
          )}

          {step < 3 && (
            <p style={{ textAlign:'center', marginTop:20, fontSize:13, color:'#6B7280' }}>
              Already have credentials?{' '}
              <Link to="/lms/login" style={{ color:'#4F8EF7', fontWeight:600 }}>Login here</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}